import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { AlertTriangle, ArrowLeft, RefreshCw, Lock, CheckCircle2 } from 'lucide-react';
import ThawMeter from './ThawMeter';
import CopyRow from './CopyRow';
import '../styles/App.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const STEP_INDEX = {
  user_deposit_pending: 0,
  pending_provider_order: 1,
  provider_order_complete: 2,
};

const STATUS_COPY = {
  user_deposit_pending: 'Waiting for your XMR deposit...',
  pending_provider_order: 'Deposit received — processing your exchange...',
  provider_order_complete: 'Sending your coins...',
  completed: 'Complete!',
  failed: 'Something went wrong with this order.',
  expired: 'This order expired before a deposit was received.',
};

const SplitNowOrderStatus = () => {
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(30);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/cex/order/${orderId}`);
      if (!res.ok) throw new Error(`We couldn't find that order (${res.status}).`);
      const data = await res.json();

      let orderData = data;
      if (data.order) orderData = data.order;
      else if (data.data) orderData = data.data;
      else if (data.result) orderData = data.result;

      setOrder(orderData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrder();
      setCountdown(30);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  useEffect(() => {
    const timer = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 30)), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="order-container">
        <div className="loading"><div className="spinner" /><p>Loading your order...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-container">
        <Link to="/" className="back-link"><ArrowLeft size={18} /> Back</Link>
        <div className="card order-card error-card">
          <AlertTriangle size={44} color="var(--red-500)" />
          <h2>We couldn't load this order</h2>
          <p>{error}</p>
          <button className="btn btn-primary" style={{ width: 'auto', padding: '0.7rem 1.5rem' }} onClick={fetchOrder}>Try again</button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-container">
        <Link to="/" className="back-link"><ArrowLeft size={18} /> Back</Link>
        <div className="card order-card error-card">
          <p>We don't have any data for this order yet.</p>
          <button className="btn btn-primary" style={{ width: 'auto', padding: '0.7rem 1.5rem' }} onClick={fetchOrder}>Try again</button>
        </div>
      </div>
    );
  }

  const orderInput = order.orderInput || {};
  const orderOutput = order.orderOutputs?.[0] || {};
  const currentStatus = order.status;
  const isComplete = currentStatus === 'completed';
  const isFailed = ['failed', 'expired'].includes(currentStatus);
  const showDeposit = (currentStatus === 'user_deposit_pending' || currentStatus === 'pending') && order.depositWalletAddress;
  const stepIndex = STEP_INDEX[currentStatus] ?? 0;

  return (
    <div className="order-container">
      <Link to="/" className="back-link"><ArrowLeft size={18} /> Back</Link>

      <div className="card order-card">
        <div className="order-header">
          <div className="privacy-pill"><Lock size={14} /><span>Private XMR swap</span></div>

          <h1>Order <span className="order-id">#{order.shortId || orderId?.slice(0, 8)}</span></h1>

          {!isFailed && <ThawMeter activeIndex={stepIndex} complete={isComplete} />}

          <p className="status-message">{STATUS_COPY[currentStatus] || currentStatus}</p>

          <div className="refresh-bar">
            <span>Auto-refresh in {countdown}s</span>
            <button onClick={fetchOrder} disabled={loading} className="refresh-inline-btn" aria-label="Refresh now">
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {showDeposit && (
          <div className="deposit-section">
            <h3>Send your XMR deposit</h3>
            <p className="deposit-instruction">
              Send exactly <strong>{order.depositAmount > 0 ? order.depositAmount : orderInput.fromAmount} XMR</strong> to the address below.
            </p>

            <div className="qr-container">
              <div className="qr-code"><QRCode value={order.depositWalletAddress} size={170} /></div>
            </div>

            <CopyRow label="XMR deposit address" value={order.depositWalletAddress} />

            <div className="warning-box">
              <AlertTriangle size={18} />
              <div className="warning-content">
                <strong>Important:</strong> Send only XMR to this address. Sending anything else will result in permanent loss.
                <div className="expires">Expires: {formatDate(order.expiredAt)}</div>
              </div>
            </div>
          </div>
        )}

        {isComplete && (
          <div className="success-section">
            <div className="icon-badge"><CheckCircle2 size={32} /></div>
            <h3>Exchange complete!</h3>
            <p>Your {orderOutput.toAssetId?.toUpperCase()} is on its way.</p>
          </div>
        )}

        {isFailed && (
          <div className="failed-section">
            <div className="icon-badge"><AlertTriangle size={32} /></div>
            <h3>{currentStatus === 'expired' ? 'Order expired' : 'Order failed'}</h3>
            <p>
              {currentStatus === 'expired'
                ? 'This order expired before a deposit was received.'
                : "Something went wrong with this exchange. If you already sent XMR, it will be returned to your refund address."}
            </p>
          </div>
        )}

        <div className="details-section">
          <h4>Order details</h4>
          <div className="details-grid">
            <div className="detail-item">
              <div className="detail-label">From</div>
              <div className="detail-value">{orderInput.fromAssetId?.toUpperCase()}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">To</div>
              <div className="detail-value">{orderOutput.toAssetId?.toUpperCase()}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">You send</div>
              <div className="detail-value">{orderInput.fromAmount} XMR</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">You receive</div>
              <div className="detail-value">
                {orderOutput.toAmount > 0 ? orderOutput.toAmount : 'Pending'} {orderOutput.toAssetId?.toUpperCase()}
              </div>
            </div>
          </div>

          <div className="address-details">
            <CopyRow variant="inline" label="Destination" value={orderOutput.toAddress} />
          </div>
        </div>

        {order.orderLegs?.[0]?.depositTxHash && (
          <div className="details-section" style={{ paddingTop: 0 }}>
            <h4>Transaction</h4>
            <CopyRow variant="inline" label="Deposit TX" value={order.orderLegs[0].depositTxHash} />
          </div>
        )}

        <div className="privacy-footer">
          <Lock size={13} />
          <span>This swap is private and untraceable. No personal data was collected.</span>
        </div>
      </div>
    </div>
  );
};

export default SplitNowOrderStatus;
